/**
 * useCursorAccumulator — pair with TanStack Query on cursor-paginated endpoints
 * so pages can render `Load N more` while keeping existing filter / sort wiring.
 *
 * Usage (inside the page component):
 *   const [cursor, setCursor] = useState<string | null>(null);
 *   useEffect(() => { setCursor(null); }, [filtersHash]);
 *
 *   const query = useQuery({
 *     queryKey: ['x', queryParams, cursor],
 *     queryFn: () => get<{items: T[]; next_cursor: string|null}>(...),
 *     placeholderData: prev => prev,
 *   });
 *
 *   const { items, nextCursor } = useCursorAccumulator(query.data, cursor, filtersHash);
 *
 *   <CursorPaginator
 *     hasMore={Boolean(nextCursor)}
 *     loading={query.isFetching}
 *     itemsShown={items.length}
 *     pageSize={pageSize}
 *     onLoadMore={() => nextCursor && setCursor(nextCursor)}
 *   />
 *
 *   // Then render `items` instead of `query.data?.items` in the table.
 *
 * Contract:
 *   - When filtersHash changes, accumulated items + nextCursor are cleared
 *     so the next data drop fully replaces the list.
 *   - When data lands with cursor=null, accumulated is replaced.
 *   - When data lands with cursor!=null, items are appended (deduped by id).
 */
import { useEffect, useRef, useState } from 'react';

export interface CursorPage<T> {
  items: T[];
  next_cursor?: string | null;
}

export interface CursorAccumulatorResult<T> {
  items: T[];
  nextCursor: string | null;
}

export function useCursorAccumulator<T extends { id: string }>(
  data: CursorPage<T> | undefined,
  cursor: string | null,
  filtersHash: string
): CursorAccumulatorResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // Guard against double-appending the same load-more response across renders.
  const lastSeenCursor = useRef<string | null | undefined>(undefined);
  // Reset surface state when filters change. We also bump this on filtersHash
  // change so the very next data drop is treated as a fresh first page.
  const filtersHashRef = useRef<string>(filtersHash);

  useEffect(() => {
    filtersHashRef.current = filtersHash;
    setItems([]);
    setNextCursor(null);
    lastSeenCursor.current = undefined;
  }, [filtersHash]);

  useEffect(() => {
    if (!data) return;
    if (cursor === null) {
      // Fresh first page (post-filter-change or initial mount).
      setItems(data.items);
      lastSeenCursor.current = null;
    } else if (cursor !== lastSeenCursor.current) {
      // Load-more landed — append, deduped by id.
      setItems((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...data.items.filter((r) => !seen.has(r.id))];
      });
      lastSeenCursor.current = cursor;
    }
    setNextCursor(data.next_cursor ?? null);
  }, [data, cursor]);

  return { items, nextCursor };
}
